import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ToastComponent } from "../../../../shared/components/toast/toast.component";
import { LogoComponent } from "../../../../shared/components/logo/logo.component";
import { CommonModule } from '@angular/common';
import { PrimaryButtonComponent } from "../../../../shared/components/primary-button/primary-button.component";
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AuthService } from '../../../../core/api/auth.service';
import { ApiErrorService } from '../../../../core/api/api-error.service';
import { HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-mfa',
  standalone: true,
  imports: [TranslocoPipe, ToastComponent, LogoComponent, CommonModule, PrimaryButtonComponent, RouterLink],
  templateUrl: './mfa.component.html',
  styleUrl: './mfa.component.css'
})
export class MfaComponent implements AfterViewInit, OnDestroy, OnInit {
  constructor(
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private apiError: ApiErrorService,
    private transloco: TranslocoService
  ) { }


  @ViewChildren('codeInput') codeInputs!: QueryList<ElementRef<HTMLInputElement>>;
  @ViewChild(ToastComponent) toast?: ToastComponent;

  code: string[] = Array(6).fill('');
  errorMessage = '';

  isSubmitting = false;
  isResending = false;

  resendSecondsLeft = 180; // 3 minutes
  private timerId: any;

  boxes = Array.from({ length: 6 });

  returnUrl: string = '/';

  maskedEmail: string = '';

  ngAfterViewInit(): void {
    // Focus first box
    this.focusIndex(0);
    this.startResendTimer();
  }

  ngOnDestroy(): void {
    if (this.timerId) clearInterval(this.timerId);
  }

  ngOnInit(): void {
    this.returnUrl = this.route.snapshot.queryParamMap.get('returnUrl') || 'dashboard';

    this.authService.sendMfa().subscribe({
      next: (response) => {
        if (response) {
          this.maskedEmail = response.email;
        }
      },
      error: async (error) => {
        if (error instanceof HttpErrorResponse) {
          if (error.error?.code === 'SVC-4022') {
            // Code already sent, ignore
            return;
          }
          const crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
          this.toast?.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
        }
      }
    });

  }

  get isCodeComplete(): boolean {
    return this.getCodeString().length === 6;
  }

  get resendFormatted(): string {
    const mm = Math.floor(this.resendSecondsLeft / 60);
    const ss = this.resendSecondsLeft % 60;
    return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
  }

  private startResendTimer(): void {
    if (this.timerId) clearInterval(this.timerId);

    this.timerId = setInterval(() => {
      this.resendSecondsLeft = Math.max(0, this.resendSecondsLeft - 1);
      if (this.resendSecondsLeft === 0) {
        clearInterval(this.timerId);
        this.timerId = null;
      }
    }, 1000);
  }

  private focusIndex(i: number): void {
    const el = this.codeInputs?.get(i)?.nativeElement;
    if (!el) return;
    el.focus();
    el.select();
  }

  private getCodeString(): string {
    const inputs = this.codeInputs?.toArray().map(x => x.nativeElement) ?? [];
    return inputs.map(i => (i.value ?? '').replace(/\D/g, '')).join('');
  }

  onKeyDown(event: KeyboardEvent, index: number): void {
    const key = event.key;
    const inputs = this.codeInputs.toArray().map(x => x.nativeElement);
    const current = inputs[index];

    // Allow shortcuts
    if (event.ctrlKey || event.metaKey) {
      const k = key.toLowerCase();

      if (k === 'v' || k === 'c' || k === 'x' || k === 'a' || k === 'z' || k === 'y') {
        return;
      }
    }

    // Allow navigation
    if (key === 'Tab' || key.startsWith('Arrow')) return;

    // Backspace
    if (key === 'Backspace') {
      event.preventDefault();

      if (current.value) {
        current.value = '';
        return;
      }

      if (index > 0) {
        inputs[index - 1].value = '';
        this.focusIndex(index - 1);
      }
      return;
    }

    // Digit typed
    if (/^\d$/.test(key)) {
      event.preventDefault(); // Insert manually
      current.value = key;
      if (index < 5) this.focusIndex(index + 1);
      return;
    }

    // Block everything else
    event.preventDefault();
  }


  onPaste(event: ClipboardEvent): void {
    // Ignore default paste logic and use our own
    event.preventDefault();
    this.errorMessage = '';

    const raw = event.clipboardData?.getData('text') ?? '';
    const digits = raw.replace(/\D/g, '').slice(0, 6);
    if (!digits) return;

    const inputs = this.codeInputs.toArray().map(x => x.nativeElement);

    for (let i = 0; i < 6; i++) {
      inputs[i].value = digits[i] ?? '';
    }

    // focus next empty, or last
    const nextEmpty = inputs.findIndex(el => !el.value);
    this.focusIndex(nextEmpty === -1 ? 5 : nextEmpty);
  }

  async confirmCode(): Promise<void> {
    const code = this.getCodeString();
    if (code.length !== 6) {
      this.errorMessage = this.transloco.translate('auth.mfa.error.code_length');
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = '';

    try {
      await firstValueFrom(this.authService.verifyMfa(code));
      this.router.navigateByUrl(this.returnUrl);
    } catch (error: any) {
      if (error instanceof HttpErrorResponse) {
        const svcCode = error.error?.code;

        // Auto-resend cases
        if (svcCode === 'SVC-4010') {
          // No active MFA code
          await this.handleAutoResend(
            this.transloco.translate('auth.mfa.toast.no_active_code_msg')
          );

          await this.autoResendCode();
          return;
        }

        if (svcCode === 'SVC-4011') {
          // MFA code expired
          await this.handleAutoResend(
            this.transloco.translate('auth.mfa.toast.code_expired_msg')
          );

          await this.autoResendCode();
          return;
        }

        if (svcCode === 'SVC-4012') {
          // Too many attempts
          await this.handleAutoResend(
            this.transloco.translate('auth.mfa.toast.too_many_attempts_msg')
          );

          await this.autoResendCode();
          return;
        }


        // Invalid code -> toast error
        if (svcCode === 'SVC-4013') {
          this.errorMessage = this.transloco.translate('auth.mfa.error.invalid_code');
          this.toast?.showToast(this.transloco.translate('shared.error'), this.transloco.translate('auth.mfa.toast.invalid_code_msg'), 'error', 8000, false);
          return;
        }

        // Fallback generic
        const crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
        this.toast?.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
        return;
      }

      this.errorMessage = this.transloco.translate('auth.mfa.error.generic');

      this.toast?.showToast(this.transloco.translate('shared.error'), this.transloco.translate('auth.mfa.toast.generic_error'), 'error', 8000, false);
    } finally {
      this.isSubmitting = false;
    }
  }

  async autoResendCode() {
    try {
      var response = await firstValueFrom(this.authService.resendMfa());
      if (response) {
        this.maskedEmail = response.email;
      }

      this.toast?.showToast(this.transloco.translate('auth.mfa.toast.resent_title'), this.transloco.translate('auth.mfa.toast.resent_msg'), 'success', 3000, false);

    } catch (error: any) {
      if (error instanceof HttpErrorResponse) {
        const crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
        this.toast?.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
        return;
      }

      this.errorMessage = this.transloco.translate('auth.mfa.error.resend_failed');
      this.toast?.showToast(this.transloco.translate('shared.error'), this.transloco.translate('auth.mfa.toast.resend_failed_msg'), 'error', 8000, false);
    }
  }

  async resendCode(): Promise<void> {
    if (this.resendSecondsLeft > 0) return;

    this.isResending = true;
    this.errorMessage = '';

    try {
      var response = await firstValueFrom(this.authService.resendMfa());
      if (response) {
        this.maskedEmail = response.email;
      }

      this.toast?.showToast(this.transloco.translate('auth.mfa.toast.resent_title'), this.transloco.translate('auth.mfa.toast.resent_msg'), 'success', 3000, false);

      this.resendSecondsLeft = 180;
      this.startResendTimer();
    } catch (error: any) {
      if (error instanceof HttpErrorResponse) {
        const crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
        this.toast?.showToast(
          await this.apiError.getErrorHeader(),
          await this.apiError.getErrorMessage(crid),
          'error',
          8000,
          false
        );
        return;
      }

      this.errorMessage = this.transloco.translate('auth.mfa.error.resend_failed');
      this.toast?.showToast(this.transloco.translate('shared.error'), this.transloco.translate('auth.mfa.toast.resend_failed_msg'), 'error', 8000, false);
    } finally {
      this.isResending = false;
    }
  }

  private async handleAutoResend(userMessage: string): Promise<void> {
    if (this.isResending) return;

    this.toast?.showToast(this.transloco.translate('shared.warning'), userMessage, 'warning', 5000, false);

    await this.resendCode();
  }


}
